// Shared shift date/time formatting for the Shift Swaps views.
// Times arrive as UTC ISO-8601 strings; render them in the viewer's locale.

export function formatShiftDate(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
}

export function formatShiftTime(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

export function formatShiftTimeRange(startIso: string, endIso: string, locale?: string): string {
  const start = formatShiftTime(startIso, locale);
  const end = formatShiftTime(endIso, locale);
  if (!start && !end) return '';
  return `${start} - ${end}`;
}

// One-line summary used inside dropdown options and compact rows.
export function formatShiftSummary(startIso: string, endIso: string, locale?: string): string {
  const date = formatShiftDate(startIso, locale);
  const range = formatShiftTimeRange(startIso, endIso, locale);
  return [date, range].filter(Boolean).join(', ');
}

// --- Local wall-clock helpers -------------------------------------------------
// Server LOCAL fields (`localStartTime`, `requesterShiftLocalStart`, …) are
// already in the company's timezone as a bare `YYYY-MM-DDTHH:mm:ss` string with
// NO 'Z'. Passing them through `new Date(iso)` would reinterpret them as UTC and
// shift the wall-clock by the browser's offset. Instead we split the string and
// feed the components to the LOCAL Date constructor, which keeps the wall-clock
// exactly as sent while still letting Intl localize the day/month/time labels.

export function parseLocalIso(localIso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(localIso);
  if (!m) return null;
  const [, y, mo, d, hh, mm, ss] = m;
  const date = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(hh),
    Number(mm),
    ss ? Number(ss) : 0,
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatLocalShiftDate(localIso: string, locale?: string): string {
  const d = parseLocalIso(localIso);
  if (!d) return '';
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
}

export function formatLocalShiftTime(localIso: string, locale?: string): string {
  const d = parseLocalIso(localIso);
  if (!d) return '';
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

export function formatLocalShiftTimeRange(localStartIso: string, localEndIso: string, locale?: string): string {
  const start = formatLocalShiftTime(localStartIso, locale);
  const end = formatLocalShiftTime(localEndIso, locale);
  if (!start && !end) return '';
  return `${start} - ${end}`;
}

export function formatLocalShiftSummary(localStartIso: string, localEndIso: string, locale?: string): string {
  const date = formatLocalShiftDate(localStartIso, locale);
  const range = formatLocalShiftTimeRange(localStartIso, localEndIso, locale);
  return [date, range].filter(Boolean).join(', ');
}

// --- Relative time / duration -------------------------------------------------

// "just now", "2 hours ago", "3 days ago", "in 2 days". Returns '' for anything a week+ out so the
// caller can fall back to the absolute date (a bare "5 weeks ago" reads worse than a date).
// `iso` MUST be a true instant (UTC, with 'Z') — createdOn / respondedOn / decision timestamps.
// Pass `justNowLabel` (e.g. t('Just now')) so anything under a minute reads as "just now" instead of
// the misleading "1 minute ago" a fresh request would otherwise show.
export function formatRelativeTime(
  iso: string,
  locale?: string,
  now: Date = new Date(),
  justNowLabel?: string,
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = d.getTime() - now.getTime();
  const abs = Math.abs(diffMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  // Sub-minute (incl. tiny clock skew that pushes a just-created row slightly into the future).
  if (abs < minute) return justNowLabel ?? '';
  const sign = diffMs <= 0 ? -1 : 1; // past => negative units ("2 hours ago")
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (abs < hour) return rtf.format(sign * Math.round(abs / minute), 'minute');
  if (abs < day) return rtf.format(sign * Math.round(abs / hour), 'hour');
  if (abs < 7 * day) return rtf.format(sign * Math.round(abs / day), 'day');
  return '';
}

// Duration of a shift in hours. Pass the true instants (UTC startTime/endTime): the two
// values are parsed identically, so the browser offset cancels and the delta is exact.
export function shiftDurationHours(startIso: string, endIso: string): number | null {
  const s = new Date(startIso).getTime();
  const e = new Date(endIso).getTime();
  if (Number.isNaN(s) || Number.isNaN(e)) return null;
  return (e - s) / (60 * 60 * 1000);
}

// --- Date bucketing for the grouped shift picker ------------------------------

export type ShiftBucket = 'today' | 'tomorrow' | 'thisWeek' | 'nextWeek' | 'later';

// Locale-neutral, rolling buckets (avoids Saturday-vs-Sunday week-start ambiguity):
// today, tomorrow, the next few days ("this week"), the following week, then everything later.
export function bucketForShiftDate(date: Date, now: Date = new Date()): ShiftBucket {
  const startOfDayMs = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((startOfDayMs(date) - startOfDayMs(now)) / dayMs);
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  if (diffDays <= 6) return 'thisWeek';
  if (diffDays <= 13) return 'nextWeek';
  return 'later';
}
